import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import FolderIcon from '~/assets/svg/newSvg/folder.svg?react';
import styles from './PhotoStorage.module.scss';

interface IAssetPhotos {
  asset_id: number;
  asset_name: string;
  photos_count: number;
}

const PhotoStorageAssets = () => {
  const { t } = useTranslation();
  const { module } = useParams<{ module: string }>();
  const navigate = useNavigate();
  const toastError = useToastError();

  const moduleName = t(`photo_storage_${module}`);

  const { data: assets, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/photo-storage/${module}/assets`
      );
      return data as IAssetPhotos[];
    },
    queryKey: ['photo-storage-assets', module],
    onError: error => toastError(error),
  });

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div className={styles.container}>
        <div className={styles.grid}>
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={styles.cardShimmer} style={{ animationDelay: `${i * 0.05}s` }} />
              ))
            : assets?.map(asset => (
              <button
                key={asset.asset_id}
                type="button"
                className={styles.card}
                onClick={() => {
                  if (module === 'accommodation' || module === 'food') {
                    navigate(`/documents/photo-storage/${module}/${asset.asset_id}/entities`);
                  } else {
                    navigate(`/documents/photo-storage/${module}/${asset.asset_id}`);
                  }
                }}
              >
                <FolderIcon className={styles.icon} />
                <p className={styles.name}>{asset.asset_name}</p>
                <span className={styles.count}>
                  {t('photos_count', { count: asset.photos_count })}
                </span>
              </button>
            ))}
          {!assets?.length && !isLoading && (
            <p className={styles.empty}>{t('no_data')}</p>
          )}
        </div>
      </div>
    </Page>
  );
};

export default PhotoStorageAssets;
